import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';

const MAP_WIDTH = 900;
const MAP_HEIGHT = 480;
const TILE_SIZE = 256;
const MAP_PADDING = 92;
const MIN_ZOOM = 14;
const MAX_ZOOM = 19;
const MAIN_ENTRANCE = {
    id: 'main-entrance',
    name: 'Главный вход',
    coordinates: {
        latitude: 55.057191,
        longitude: 82.887192,
    },
};

const getCoordinates = (animal) => {
    const latitude = Number(animal?.geolocation?.[0]);
    const longitude = Number(animal?.geolocation?.[1]);
    if (
        !Number.isFinite(latitude)
        || !Number.isFinite(longitude)
        || Math.abs(latitude) > 90
        || Math.abs(longitude) > 180
        || (latitude === 0 && longitude === 0)
    ) {
        return null;
    }
    return { latitude, longitude };
};

const toWorldPoint = ({ latitude, longitude }, zoom) => {
    const scale = TILE_SIZE * (2 ** zoom);
    const latitudeRadians = latitude * Math.PI / 180;
    const sinLatitude = Math.min(Math.max(Math.sin(latitudeRadians), -0.9999), 0.9999);
    return {
        x: ((longitude + 180) / 360) * scale,
        y: (0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI)) * scale,
    };
};

const chooseZoom = (points) => {
    for (let zoom = MAX_ZOOM; zoom >= MIN_ZOOM; zoom -= 1) {
        const worldPoints = points.map((point) => toWorldPoint(point, zoom));
        const xs = worldPoints.map((point) => point.x);
        const ys = worldPoints.map((point) => point.y);
        const width = Math.max(...xs) - Math.min(...xs);
        const height = Math.max(...ys) - Math.min(...ys);
        if (width <= MAP_WIDTH - MAP_PADDING * 2 && height <= MAP_HEIGHT - MAP_PADDING * 2) {
            return zoom;
        }
    }
    return MIN_ZOOM;
};

const buildMapModel = (animals, isCircular) => {
    const locatedAnimals = [];
    const missingAnimals = [];

    animals.forEach((animal, sourceIndex) => {
        const coordinates = getCoordinates(animal);
        if (coordinates) {
            locatedAnimals.push({ animal, coordinates, sourceIndex });
        } else {
            missingAnimals.push(animal);
        }
    });

    if (locatedAnimals.length < 1) {
        return { locatedAnimals, missingAnimals, tiles: [], routePoints: [], zoom: null };
    }

    const routeStops = [
        {
            key: MAIN_ENTRANCE.id,
            kind: 'entrance',
            name: MAIN_ENTRANCE.name,
            coordinates: MAIN_ENTRANCE.coordinates,
        },
        ...locatedAnimals.map((item, index) => ({
            ...item,
            key: `animal-${item.animal.id}`,
            kind: 'animal',
            order: index + 1,
            name: item.animal.name || `Животное #${item.animal.id}`,
        })),
    ];
    if (isCircular) {
        routeStops.push({
            key: 'main-entrance-finish',
            kind: 'finish',
            name: 'Финиш у главного входа',
            coordinates: MAIN_ENTRANCE.coordinates,
        });
    }
    const zoom = chooseZoom(routeStops.map((item) => item.coordinates));
    const worldPoints = routeStops.map((item) => ({
        ...item,
        world: toWorldPoint(item.coordinates, zoom),
    }));
    const xs = worldPoints.map((point) => point.world.x);
    const ys = worldPoints.map((point) => point.world.y);
    const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
    const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;
    const originX = centerX - MAP_WIDTH / 2;
    const originY = centerY - MAP_HEIGHT / 2;
    const tileLimit = 2 ** zoom;
    const minTileX = Math.max(0, Math.floor(originX / TILE_SIZE));
    const maxTileX = Math.min(tileLimit - 1, Math.floor((originX + MAP_WIDTH) / TILE_SIZE));
    const minTileY = Math.max(0, Math.floor(originY / TILE_SIZE));
    const maxTileY = Math.min(tileLimit - 1, Math.floor((originY + MAP_HEIGHT) / TILE_SIZE));
    const tiles = [];

    for (let tileX = minTileX; tileX <= maxTileX; tileX += 1) {
        for (let tileY = minTileY; tileY <= maxTileY; tileY += 1) {
            tiles.push({
                key: `${zoom}-${tileX}-${tileY}`,
                href: `https://tile.openstreetmap.org/${zoom}/${tileX}/${tileY}.png`,
                x: tileX * TILE_SIZE - originX,
                y: tileY * TILE_SIZE - originY,
            });
        }
    }

    return {
        locatedAnimals,
        missingAnimals,
        tiles,
        zoom,
        routePoints: worldPoints.map((point) => ({
            ...point,
            x: point.world.x - originX,
            y: point.world.y - originY,
        })),
    };
};

const RouteMapPreview = ({ animals = [], isCircular = false }) => {
    const model = useMemo(() => buildMapModel(animals, isCircular), [animals, isCircular]);
    const pointCount = model.locatedAnimals.length;

    if (pointCount < 1) {
        return (
            <Box className="route-map-empty" role="status">
                <Box className="route-map-empty-icon"><LocationOffOutlinedIcon /></Box>
                <Typography component="h3">Недостаточно точек для маршрута</Typography>
                <Typography>
                    Добавьте хотя бы одно животное с заполненными координатами. Маршрут начнётся от главного входа.
                </Typography>
            </Box>
        );
    }

    const polylinePoints = model.routePoints.map((point) => `${point.x},${point.y}`).join(' ');

    return (
        <Box className="route-map-preview">
            <Box className="route-map-canvas" aria-label={`Карта ${isCircular ? 'кольцевого ' : ''}маршрута от главного входа к ${pointCount} животным`}>
                <svg
                    viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
                    role="img"
                    aria-labelledby="route-map-title route-map-description"
                    preserveAspectRatio="xMidYMid slice"
                >
                    <title id="route-map-title">{isCircular ? 'Кольцевой маршрут' : 'Маршрут'} от главного входа по выбранным животным</title>
                    <desc id="route-map-description">Маршрут начинается у главного входа, соединяет животных в выбранном порядке{isCircular ? ' и возвращается ко входу' : ''}.</desc>
                    <rect width={MAP_WIDTH} height={MAP_HEIGHT} className="route-map-background" />
                    {model.tiles.map((tile) => (
                        <image
                            key={tile.key}
                            href={tile.href}
                            x={tile.x}
                            y={tile.y}
                            width={TILE_SIZE}
                            height={TILE_SIZE}
                        />
                    ))}
                    <polyline points={polylinePoints} className="route-map-line-outline" />
                    <polyline points={polylinePoints} className="route-map-line" />
                    {model.routePoints.map((point) => point.kind === 'entrance' ? (
                        <g key={point.key} transform={`translate(${point.x} ${point.y})`} className="route-map-start-marker">
                            <title>Старт: главный вход</title>
                            <circle r="22" className="route-map-marker-shadow" />
                            <circle r="19" className="route-map-start-circle" />
                            <path d="M -5 10 V -11 M -4 -10 H 9 L 5 -3 H -4" />
                        </g>
                    ) : point.kind === 'finish' ? (
                        <g key={point.key} transform={`translate(${point.x} ${point.y})`} className="route-map-start-marker">
                            <title>Финиш: главный вход</title>
                            <circle r="22" className="route-map-marker-shadow" />
                            <circle r="19" className="route-map-start-circle" />
                            <text textAnchor="middle" dominantBaseline="central">F</text>
                        </g>
                    ) : (
                        <g key={point.key} transform={`translate(${point.x} ${point.y})`} className="route-map-marker">
                            <title>{`${point.order}. ${point.name}`}</title>
                            <circle r="20" className="route-map-marker-shadow" />
                            <circle r="17" className="route-map-marker-circle" />
                            <text textAnchor="middle" dominantBaseline="central">{point.order}</text>
                        </g>
                    ))}
                </svg>
                <Box className="route-map-points-badge">Старт · {pointCount} животных{isCircular ? ' · Финиш' : ''}</Box>
                <a
                    className="route-map-attribution"
                    href="https://www.openstreetmap.org/copyright"
                    target="_blank"
                    rel="noreferrer"
                >
                    © OpenStreetMap
                </a>
            </Box>

            <Box component="ol" className="route-map-legend" aria-label="Порядок точек маршрута">
                {model.routePoints.map((point) => (
                    <Box
                        component="li"
                        key={point.key}
                        className={`route-map-legend-item${point.kind === 'entrance' || point.kind === 'finish' ? ' is-start' : ''}`}
                    >
                        <Box component="span" className="route-map-legend-number">
                            {point.kind === 'entrance' ? 'S' : point.kind === 'finish' ? 'F' : point.order}
                        </Box>
                        <Typography title={point.name}>{point.name}</Typography>
                    </Box>
                ))}
            </Box>

            {model.missingAnimals.length > 0 && (
                <Typography className="route-map-warning" role="status">
                    Не показаны без координат: {model.missingAnimals.map((animal) => animal.name || `ID ${animal.id}`).join(', ')}
                </Typography>
            )}
        </Box>
    );
};

export default RouteMapPreview;
